<template>
  <div>
    <div class="d-flex align-center py-3">
      <h2 class="flex-grow-1">{{ title }}</h2>
      <v-btn color="primary" small @click="openDialog()">Add</v-btn>
    </div>

    <!-- Table -->
    <v-data-table :headers="headers" :items="questions" item-value="id" class="elevation-1" hide-default-footer>
      <template #[`item.image`]="{ item }">
        <v-avatar size="40">
          <v-img :src="item.image" />
        </v-avatar>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-btn icon small color="primary" @click="openDialog(item)">
          <v-icon>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Form Modal -->
    <v-dialog v-model="dialog" max-width="900px">
      <v-card>
        <v-card-title>
          {{ Form.id ? 'Edit Question' : 'Add Question' }}
          <v-spacer />
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-form ref="formRef" v-model="Form.valid" lazy-validation>
          <v-card-text>
            <!-- Titles and Instructions -->
            <v-row v-for="field in textFields" :key="field.key" class="mt-4">
              <v-col cols="12" md="6" v-for="lang in languages" :key="lang" class="py-0">
                <component
                  :is="field.type"
                  v-model="Form[field.key][lang]"
                  :label="`${field.label} ${lang.toUpperCase()}`"
                  dense
                  outlined
                  :rules="[requiredRule]"
                  v-bind="field.props"
                />
              </v-col>
            </v-row>

            <!-- Options -->
            <div class="mt-4">
              <h4>Options</h4>
              <v-row v-for="(option, index) in Form.options" :key="option.id" align="center">
                <v-col cols="4" v-for="lang in languages" :key="lang">
                  <v-text-field
                    v-model="option.label[lang]"
                    :label="`Label ${lang.toUpperCase()}`"
                    dense
                    outlined
                    :rules="[requiredRule]"
                  />
                </v-col>
                <v-col cols="2">
                  <v-text-field
                    v-model="option.value"
                    label="Value"
                    type="number"
                    dense
                    outlined
                    :rules="[requiredRule]"
                  />
                </v-col>
                <v-col cols="1">
                  <v-btn icon color="error" @click="removeOption(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-btn small color="secondary" @click="addOption">Add Option</v-btn>
              <p v-if="optionError" class="text-error text-subtitle-2 mt-2" style="color: red">{{ optionError }}</p>
            </div>

            <!-- Image -->
            <div class="d-flex flex-column align-center mt-4">
              <v-img :src="selectImageBase64 || Form.image" max-width="150" class="mb-2 image-border" />
              <v-btn small color="secondary" @click="selectImage">Change Image</v-btn>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" :disabled="isSubmitDisabled" @click="onSubmit">
              {{ Form.id ? 'Save' : 'Add' }}
            </v-btn>
            <v-btn v-if="Form.id" text color="error" @click="deleteQuestion(Form.id)">Delete</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <input type="file" ref="fileInput" style="display: none" accept="image/*" @change="onFileChange" />
  </div>
</template>

<script>
import Form from '../../../form/form';
import { srcToBase64 } from '../../../utils/stringUtil';

export default {
  props: {
    title: { type: String, required: true },
    questions: { type: Array, required: true },
    assessmentId: { type: Number, required: true },
  },
  data() {
    return {
      dialog: false,
      requiredRule: (v) => (v && v.toString().trim().length > 0) || 'Required',
      languages: ['th', 'en'],
      textFields: [
        { key: 'title', label: 'Title', type: 'v-text-field' },
        { key: 'instruction', label: 'Instruction', type: 'v-text-field' },
      ],
      Form: new Form({
        data() {
          return {
            id: null,
            title: { th: '', en: '' },
            instruction: { th: '', en: '' },
            options: [],
            image: '',
            valid: false,
          };
        },
      }),
      selectImageBase64: null,
      selectImageFileName: null,
      optionError: '',
    };
  },
  computed: {
    headers() {
      return [
        { text: 'ID', value: 'id', align: 'center' },
        { text: 'Title', value: 'title[th]' },
        { text: 'Instruction', value: 'instruction[th]' },
        { text: 'Type', value: 'type' },
        { text: 'Image', value: 'image', align: 'center' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ];
    },
    isSubmitDisabled() {
      const titleTh = (this.Form.title?.th || '').trim();
      const titleEn = (this.Form.title?.en || '').trim();
      const instructionTh = (this.Form.instruction?.th || '').trim();
      const instructionEn = (this.Form.instruction?.en || '').trim();

      const validOptions =
        this.Form.options.length >= 2 &&
        this.Form.options.every(
          (option) =>
            option.value !== null && option.value !== '' && option.label?.th?.trim() && option.label?.en?.trim()
        );

      return !titleTh || !titleEn || !instructionTh || !instructionEn || !validOptions;
    },
  },
  methods: {
    openDialog(question = null) {
      if (question) {
        this.Form = JSON.parse(JSON.stringify(question));
      } else {
        this.Form = {
          id: null,
          title: { th: '', en: '' },
          instruction: { th: '', en: '' },
          options: [],
          image: '',
          valid: false,
        };
      }
      this.dialog = true;
      this.selectImageBase64 = null;
      this.selectImageFileName = null;
      this.optionError = '';
      this.$nextTick(() => {
        if (this.$refs.formRef) this.$refs.formRef.resetValidation();
      });
    },
    closeDialog() {
      this.selectImageBase64 = null;
      this.selectImageFileName = null;
      this.dialog = false;
    },
    onSubmit() {
      const isValid = this.$refs.formRef.validate();
      if (!isValid) return;

      const hasEnoughOptions = this.Form.options.length >= 2;

      const optionsAreValid =
        hasEnoughOptions &&
        this.Form.options.every(
          (option) =>
            option.value !== null && option.value !== '' && option.label?.th?.trim() && option.label?.en?.trim()
        );

      if (!optionsAreValid) {
        this.optionError = 'Question must have at least 2 fully completed options.';
        return;
      }

      this.optionError = '';

      const body = {
        title: this.Form.title,
        instruction: this.Form.instruction,
        options: this.Form.options,
        assessmentId: this.assessmentId,
      };

      if (this.selectImageBase64) {
        body.image = {
          fileName: this.selectImageFileName,
          base64: srcToBase64(this.selectImageBase64),
        };
      }

      if (this.Form.id) {
        this.$emit('onUpdate', this.Form.id, body);
      } else {
        this.$emit('onAdd', body);
      }

      this.dialog = false;
    },
    deleteQuestion(id) {
      this.$emit('onDelete', id);
      this.dialog = false;
    },
    addOption() {
      this.Form.options.push({
        id: Date.now(),
        label: { th: '', en: '' },
        value: this.Form.options.length,
      });
    },
    removeOption(index) {
      this.Form.options.splice(index, 1);
    },
    selectImage() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.selectImageFileName = file.name;
        this.selectImageBase64 = ev.target.result;
      };
      reader.readAsDataURL(file);
    },
  },
};
</script>

<style scoped>
.image-border {
  border: 1px dashed grey;
  border-radius: 8px;
}
</style>
